import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function ImagePreviewDialog({ open, onClose, imageBase64, title }) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const zoomAmount = e.deltaY * -0.001;
    let newScale = Math.min(Math.max(0.8, scale + zoomAmount), 5);
    setScale(newScale);
  };

  const handleMouseDown = (e) => {
    setDragging(true);
    setLastPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - lastPos.x, y: e.clientY - lastPos.y });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setDragging(true);
      setLastPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();

    if (e.touches.length === 2) {
      const dist = (touch) => Math.hypot(touch[0].clientX - touch[1].clientX, touch[0].clientY - touch[1].clientY);
      const newDistance = dist(e.touches);
      const prevDistance = imgRef.current.lastDistance || newDistance;
      const zoom = (newDistance - prevDistance) * 0.005;
      let newScale = Math.min(Math.max(0.8, scale + zoom), 5);
      setScale(newScale);
      imgRef.current.lastDistance = newDistance;
      return;
    }

    if (!dragging) return;
    setPosition({
      x: e.touches[0].clientX - lastPos.x,
      y: e.touches[0].clientY - lastPos.y,
    });
  };

  const handleTouchEnd = () => {
    setDragging(false);
    imgRef.current.lastDistance = null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: "1px solid #ddd",
        }}
      >
        <Typography fontWeight={600} fontSize="1.1rem" textAlign="center" flex={1}>
          {title}
        </Typography>

        <IconButton onClick={onClose} sx={{ color: "red" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        ref={containerRef}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          background: "#000",
          touchAction: "none",
          height: { xs: "60vh", sm: "70vh", md: "80vh" },
        }}
        onWheel={handleWheel}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imgRef}
          src={imageBase64}
          alt="Preview"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            maxWidth: "100%",
            maxHeight: "100%",
            cursor: dragging ? "grabbing" : "grab",
            transition: dragging ? "none" : "transform 0.15s ease-out",
          }}
          onDoubleClick={resetZoom}
        />
      </DialogContent>
    </Dialog>
  );
}

export default ImagePreviewDialog;